import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { App, Button, Dropdown, Input, Modal, Segmented, Select, Table, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { CalendarClock } from 'lucide-react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router';
import { useState } from 'react';
import {
  ARRANGEMENT_ANY_DATE_WINDOW,
  ARRANGEMENT_STATUSES_ISSUED,
  ARRANGEMENT_TYPE_ANY,
  ARRANGEMENT_TYPES,
  BULK_DISCARD_MAX,
  isArrangementType,
  searchHintPatch,
} from '@pams/shared';
import type {
  ArrangementDto,
  ArrangementListItemDto,
  ArrangementType,
  ArrangementListPageDto,
  BulkDiscardResultDto,
  Paginated,
} from '@pams/shared';
import { ARRANGEMENT_SORT_FIELDS, istDutyDate, readableDate } from '@pams/shared';
import { DatePicker } from '@/components/DatePicker';
import { FilterField } from '@/components/FilterField';
import { PageHeader } from '@/components/PageHeader';
import { StatusDot } from '@/components/StatusDot';
import { StatusPill } from '@/components/StatusPill';
import { useAuth } from '@/hooks/useAuth';
import { ColumnsMenu } from '@/components/ColumnsMenu';
import { columnKeySet } from '@/lib/tableColumns';
import { useColumnVisibility } from '@/hooks/useColumnVisibility';
import { useServerTable } from '@/hooks/useServerTable';
import {
  can,
  mayAuthorArrangement,
  mayBulkDiscardDrafts,
  mayFilterByDivision,
  mayUseTemplates,
  READ_ONLY_TOOLTIP,
  actingOrgUnitTypeOf,
} from '@/lib/accessRole';
import { IN_CHARGE_UNNAMED_LABEL, arrangementTypeLabel, templateSourceLabel } from '@/lib/labels';
import { useArrangementAuthoringSetting } from '@/hooks/useArrangementAuthoringSetting';
import { useArrangementListFilters } from '@/hooks/useArrangementListFilters';
import { useDivisionOptions } from '@/hooks/useDivisionOptions';
import { sameListFilters, type ArrangementListFilters } from '@/lib/arrangementListFilters';
import { api, type ApiError } from '@/lib/api';
import { asPickerDate } from '@/lib/pickerDate';
import {
  fillSummary,
  orderStatusWord,
  statusFilterOptions,
  statusFilterQuery,
  statusFilterWord,
} from '@/lib/statusPresentation';
import { queryKeys } from '@/lib/queryKeys';
import { listOriginFor, withListOrigin } from '@/lib/listOrigin';
import { typedListHref } from '@/lib/templateLinks';
import { groupRows, isPartialGroup, type GroupedRow } from '@/lib/arrangementGroups';
import { ARRANGEMENT_SEARCH_FIELD_CLASS, ARRANGEMENT_SEARCH_READS, searchTermOf } from '@/lib/arrangementSearch';
import { RaiserPill } from '@/components/RaiserPill';
import { NameCell } from '@/components/NameCell';
import { ARCHIVE_COPY } from '@/components/archive/copy';
import { useArchiveArrangement } from '@/components/archive/useArchiveArrangement';
import {
  ARRANGEMENT_KIND_LABEL,
  ARRANGEMENTS_COPY,
  DISCARD_MODE_COPY,
  GROUP_COPY,
  LIBRARY_COPY,
  SEARCH_HINT_COPY,
  TYPED_TAB_COPY,
} from './copy';
import { ArrangementFiltersModal } from './components/ArrangementFiltersModal';
import { LibraryTable } from './components/LibraryTable';
import { ListFiltersButton } from './components/ListFiltersButton';
import { SearchEmptyState, type SearchEmptyPage } from './components/SearchEmptyState';
import { UploadOrderModal } from './components/UploadOrderModal';
import {
  ARRANGEMENT_LIST_FILTER_KEYS,
  listFiltersSummary,
  partitionSearchHintPatch,
  relaxSessionFilters,
  sessionFilterQuery,
} from './listFilters';

/**
 * ⚠️ [C5b §6] THE PAGE THE TABLE HOLDS — its rows as RENDERED, plus the search hint and the term
 * that produced it. `useServerTable`'s `TPage`; everything the hook reads is `Paginated`'s.
 */
type ArrangementsTablePage = Paginated<GroupedRow> & SearchEmptyPage;

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  THE ARRANGEMENT LIST — `useServerTable`'s EIGHTH consumer, and still not one line of
 *  the hook has changed.
 *
 *  ⚠️ THE DATE RANGE RIDES THE HOOK'S EXISTING `filterKeys`, AS TWO OPAQUE STRINGS.
 *
 *  `filters` is `Record<string, string>` and the values are documented as opaque — ids,
 *  enum members. Two keys, `dateFrom` and `dateTo`, are two opaque strings, so a range
 *  needs no hook change at all. Teaching the hook about ranges would be the first change
 *  in eight consumers, and it would be for a shape only this page has.
 *
 *  ⚠️ WHAT "PREPARED FOR A SECOND TAB" MEANS IN CODE, AND WHAT IT DOES NOT.
 *  `ARRANGEMENT_KINDS` exists, every row carries `STANDARD`, the list endpoint filters on
 *  it and the index leads with the scope so a second value is served by an index that
 *  already exists. P3b adds a tab that sets one filter. Nothing here renders a tab, and
 *  nothing here knows what round-the-clock means — building the seam is not building the
 *  feature.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [P7B.1 §4] ONE LIST COMPONENT, TWO ADDRESSES. Manage Arrangements renders it untyped at
 *  `/arrangements`; a typed arrangement (`durga-puja-2026`) renders it at `/pa/:typeKey` with
 *  `fixedType`, from its own sidebar row (`pages/TypedArrangements`). The fourth option P7B put on
 *  the `Segmented` is gone, and so is `type` as a URL filter: the TYPE is the route now.
 *
 *  ⚠️ WITH `fixedType` THE PAGE IGNORES `view`, `kind` AND `standingOrderId` — a typed order is
 *  `STANDARD` and is never an uploaded PA — so the `Segmented` and "Upload a PA" are not rendered,
 *  and a stray `?kind=` in a hand-typed link cannot empty the list. The request is P7B's typed
 *  request exactly: `type`, plus the any-date window when no bound is set.
 *
 *  ⚠️ AND THE QUERY KEY CARRIES THE TYPE, so the typed and untyped lists never share a cache entry
 *  whose params happen to match — page one of each is otherwise the same key. ⚠️ [amended TYPE-FILTER]
 *  UNDER ITS OWN NAME, `fixedType`, never `type`: Manage Arrangements narrowed to "Durga Puja PA only"
 *  carries `type: 'durga-puja-2026'` in ITS key, and the two requests differ (the typed list adds the
 *  any-date window and drops `kind`) — one name would hand the typed page the other list's rows, for as
 *  long as the 30-second `staleTime` lasts.
 *
 *  ⚠️ [LIST-FILTERS] …AND THE SESSION FILTER, WHEN ONE IS APPLIED. "Adjust filters" is held per
 *  officer in the browser, not in the URL, so `params` cannot carry it; `sessionFilterQuery` is spread
 *  into the key AND the request from one value read once per render, so the two cannot disagree. It
 *  answers `{}` with nothing applied, which hashes exactly as before — every no-filter key, on both
 *  lists, is unchanged.
 *
 *  ⚠️ [TYPED-BACK] AND A TYPED LIST HANDS ITS OWN ADDRESS TO EVERY ORDER IT OPENS — `?from=` on the row's
 *  name link and on the corrigendum's navigation (`origin`, below), so the record, the builder and
 *  every act that leaves the order come back HERE, page, sort and filters included. Untyped, `origin` is
 *  `null` and every href is byte-identical to what it was — ⚠️ [amended TYPE-FILTER] and Manage
 *  Arrangements now lists EVERY order by default ("Order type": All), so it is the way back of every
 *  order opened with no list behind it, typed or not (`listAfterLeaving`).
 *  ⚠️ [amended LIST-FILTERS] "Filters" means the URL's: the session filter follows the OFFICER rather
 *  than the page, so it never enters `?from=` — the list it leads back to re-applies it from the store.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const ArrangementsList = ({ fixedType }: { fixedType?: ArrangementType }): React.ReactElement => {
  const { officer } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { message, modal } = App.useApp();
  const [copyOpen, setCopyOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  /*
   * ─────────────────────────────────────────────────────────────────────────────────
   *  ⚠️ [LIST-FILTERS] "ADJUST FILTERS" — ONE VALUE PER OFFICER, FOR BOTH ORDER LISTS, EVERY TAB,
   *  UNTIL SIGN-OUT (`hooks/useArrangementListFilters.ts`). Read BEFORE `useServerTable`, because its
   *  `queryKey` and `queryFn` close over it, and read on the FIRST render — so a stored filter is on
   *  the first request and the list never paints unfiltered first.
   *
   *  ⚠️ `sessionQuery` IS READ ONCE, HERE, and both closures use it: the key a page is cached under
   *  and the request that fills it come from one value.
   * ─────────────────────────────────────────────────────────────────────────────────
   */
  const listFilters = useArrangementListFilters(officer?.id, fixedType);
  const sessionQuery = sessionFilterQuery(listFilters.filters, fixedType);
  const [filtersOpen, setFiltersOpen] = useState(false);
  /*
   * ⚠️ THE NAMES FOR THE BUTTON'S DESCRIPTION, AND ONLY WHILE A DIVISION IS APPLIED. The tree is asked
   * for then (or while the dialog shows Division, which asks for it itself) and never otherwise — an
   * applied Order type alone needs no names — so every request sequence this list's tests assert is
   * byte-identical when nobody touches the filter.
   */
  const divisionNames = useDivisionOptions({ enabled: listFilters.divisionsActive });
  /*
   * ⚠️ SELECTION IS PAGE-LOCAL STATE, NEVER URL STATE. `useServerTable` owns the URL and its
   * `filterKeys` are the SERVER QUERY; a selection is neither shareable nor a filter, and
   * twenty ObjectIds in the address bar is not a design.
   *
   * ⚠️ [amended P7B §1d] THIS COMMENT SAID THE SELECTION "RESETS ON A PAGE OR FILTER CHANGE
   * DELIBERATELY", AND IT DOES NOT. It is cleared by exactly three things: switching the tab (the
   * `Segmented`'s handler), `Clear`, and `invalidateAll` after a group write. A page, sort or filter
   * change leaves it standing — the keys survive in state while their rows leave the screen, and the
   * selection bar keeps counting them. Corrected here rather than "fixed", because the behaviour is
   * what has shipped and the P7B pass changes nothing about grouping; the discard mode below is the
   * selection that DOES clear on every one of those changes, by construction.
   */
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  /*
   * ─────────────────────────────────────────────────────────────────────────────────
   *  ⚠️ [P7B §6a] THE HOLDER'S DISCARD MODE — A SECOND SELECTION, NEVER THE GROUPING ONE.
   *
   *  Offered only to a `master_superadmin` holder (`mayBulkDiscardDrafts`, the route's own
   *  requirement), on both order tabs. While it is on, the table's checkboxes select DRAFTS to
   *  discard and nothing else; while it is off, the grouping selection is exactly what it was.
   *
   *  ⚠️ IT CLEARS ON ANY PAGE, SORT, FILTER, SEARCH OR TAB CHANGE — and stays cleared on the way back.
   *  The table's parameters are remembered beside the selection, and the moment they differ the
   *  selection is dropped DURING RENDER (React's documented "adjust state when a prop changes",
   *  `useServerTable`'s own search-box idiom) — never in an effect, which would paint one frame of a
   *  selection over rows that have already left. Returning to page one does NOT bring the old
   *  selection back. A destructive act over rows the operator can no longer see is the one thing
   *  this must not allow.
   * ─────────────────────────────────────────────────────────────────────────────────
   */
  const [discardMode, setDiscardMode] = useState(false);
  const [discardPick, setDiscardPick] = useState<string[]>([]);
  const [discardResult, setDiscardResult] = useState<
    { result: BulkDiscardResultDto; titles: Readonly<Record<string, string>> } | undefined
  >();
  /*
   * ⚠️ COLLAPSE IS OPT-IN, WHICH IS WHY THE STATE HOLDS WHAT IS *CLOSED*. A collapsed group
   * removes rows from the DOM, so the list has to read exactly as it did before grouping
   * existed, plus a band. Defaulting to "expanded" by tracking the exceptions also survives
   * the data arriving asynchronously, which `defaultExpandAllRows` does not.
   */
  const [collapsedGroups, setCollapsedGroups] = useState<string[]>([]);
  const [groupOpen, setGroupOpen] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [renaming, setRenaming] = useState<{ id: string; name: string } | undefined>();
  const canWrite = officer ? can(officer.accessRole, 'arrangement.write') : false;
  /* ⚠️ [C3a §8] The archive VERB, not the write verb — `arrangement.delete` is what
     `POST /:id/archive` is gated on, and the two differ for a role added later. */
  const canDelete = officer ? can(officer.accessRole, 'arrangement.delete') : false;
  /* ⚠️ [UX1 §8] The SAME predicate `RequireDivision` and `assertMayRaiseArrangement` use. */
  const { nodalsMayAuthor } = useArrangementAuthoringSetting();
  const mayRaise = officer
    ? mayAuthorArrangement(officer.accessRole, actingOrgUnitTypeOf(officer), nodalsMayAuthor)
    : false;
  /* [P7B §4] The template pages' door — a commissionerate seat. */
  const templateAuthor = mayUseTemplates(officer);
  /* [P7B §6a] The `master_superadmin` override, asked through the one resolver in `accessRole.ts`. */
  const mayDiscardMany = mayBulkDiscardDrafts(officer);

  /**
   * ⚠️ "START FROM A PREVIOUS PA" SAVES MORE OPERATOR TIME THAN EVERY OTHER OPTIMISATION
   * ON THE INTAKE COMBINED, because bandobast repeats: same places, same policy, same
   * divisions, week after week.
   *
   * The copy happens SERVER-SIDE, so the new order is a real draft with its own reference
   * before the operator sees a single field — and so re-pointing at the policy's current
   * published version is decided in one place rather than by whatever the form happened to
   * be holding.
   */
  const recent = useQuery({
    queryKey: queryKeys.arrangements.list({ recent: true }),
    queryFn: ({ signal }) =>
      api.get<Paginated<ArrangementListItemDto>>('/arrangements', {
        /*
         * ⚠️ [C3c.1] AN EXPLICIT WINDOW, BECAUSE §8a's DEFAULT IS FORWARD AND THIS PICKER IS
         * ABOUT THE PAST. Sending no dates gets today..+7, so every order that has already run —
         * which is nearly all of them, and exactly what somebody copying wants — was silently
         * absent and the modal read "There are no earlier arrangements yet."
         */
        query: { page: 1, limit: 10, sortBy: 'paDate', sortOrder: 'desc', ...ARRANGEMENT_ANY_DATE_WINDOW },
        signal,
      }),
    enabled: copyOpen,
  });

  const duplicate = useMutation({
    mutationFn: (id: string) => api.post<ArrangementDto>(`/arrangements/${id}/duplicate`, {}),
    onSuccess: (copy) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.arrangements.all });
      setCopyOpen(false);
      void navigate(`/arrangements/${copy.id}/edit`);
    },
    onError: (error: ApiError) => void message.error(error.message),
  });

  /*
   * ⚠️ THE HOOK IS INSTANTIATED ON THE *RENDERED* ROW TYPE, and the assembly happens inside
   * `queryFn`. That is what makes everything else fall out cleanly: `tableProps` is already
   * `TableProps<GroupedRow>` and spreads with no cast, `pagination.total` stays the SERVER's
   * arrangement count (a band is chrome and must not be counted), and `useServerTable` gains
   * a ninth consumer with not one line changed.
   */
  const table = useServerTable<GroupedRow, (typeof ARRANGEMENT_SORT_FIELDS)[number], ArrangementsTablePage>({
    sortFields: ARRANGEMENT_SORT_FIELDS,
    /*
     * ⚠️ [amended C5c §9] THIS PAGE DEFAULTS TO THE DAY OF THE LAST UPDATE, NEWEST FIRST, THEN THE
     * DUTY DATE WITHIN THE DAY (the client's decision, U8) — replacing [C3c §8a]'s operational,
     * ascending "what is on next". The SERVER's own default is unchanged (`firstDutyDate`,
     * ascending): this page asks for the new order explicitly, so every other caller of
     * `GET /arrangements` reads exactly what it did.
     *
     * ⚠️ THE WINDOW IS STILL THE SERVER'S. Which orders are listed is decided by its default
     * operational window (today plus seven days), never by a date this page writes into its own
     * URL: a client-written default would bake today into every shared link. The sort only
     * reorders what that window admits.
     *
     * ⚠️ AND `paDate` IS STILL REACHABLE, THROUGH THE SORT CONTROL BELOW. That matters more than
     * it looks: `arrangementGroups.ts` nests a group's members only while the list is sorted by
     * ORDER date, because that is the only sort under which they are adjacent. §8b hides the
     * `paDate` COLUMN, which carried the only sorter for it — so without the control, corrigendum
     * families and manual groups would stop being visible anywhere in the product.
     */
    defaultSortBy: 'updatedDay',
    defaultSortOrder: 'desc',
    /* ⚠️ [C5b §6] ONE LIST, TWO READERS — the hook, and the search hint's reset. See `listFilters.ts`. */
    filterKeys: ARRANGEMENT_LIST_FILTER_KEYS,
    /* ⚠️ [LIST-FILTERS] `sessionQuery` is `{}` with nothing applied — see the header. */
    queryKey: (params) =>
      queryKeys.arrangements.list({ ...params, ...(fixedType ? { fixedType } : {}), ...sessionQuery }),
    rowKey: (row) => row.key,
    /*
     * ⚠️ [amended C5b §6] THE ORDERS QUERY NO LONGER RUNS WHILE THE LIBRARY IS ON SCREEN.
     *
     * [C3a §2] stated the cost of letting it run: one already-paginated list request, because
     * `useServerTable`'s `enabled` is a BOOLEAN evaluated at the call and `view` is only known from
     * the params the hook itself returns. That argument still holds for `enabled` — but the
     * `queryFn` RECEIVES those params, so it can decline the request itself. It has to now: the
     * library keeps its own `lib_search`, so an orders search the operator left behind stays in the
     * URL, and every request for a table nobody can see would run the search hint's probes as well —
     * up to seven `exists` queries a request, for an answer nobody is looking at. An empty page stands
     * in for it; the hook is still untouched.
     */
    queryFn: async (params, signal) => {
      /* ⚠️ [C3a §2] `view` IS THIS PAGE'S, NOT THE SERVER'S — see `listFilters.ts`. */
      const { view, status, kind, standingOrderId, ...serverFilters } = params.filters;
      if (view === 'library' && !fixedType)
        return { items: [], total: 0, page: params.page, limit: params.limit, pages: 0 };
      /* ⚠️ [C5c §7] TRIMMED, AND NOT SENT WHEN BLANK — the server's `.trim().min(1)` would 422 a space. */
      const search = searchTermOf(params.search);
      /*
       * ─────────────────────────────────────────────────────────────────────────────────
       *  ⚠️ [P7B §5] THE TYPED TAB IS A `type` AND NOTHING ELSE CHANGES ON THE OTHER TWO.
       *
       *  A registered key sends `type` and DROPS `kind` and `standingOrderId` — a typed order is
       *  `STANDARD`, so Round the clock → Durga Puja PA would otherwise send `kind=ROUND_THE_CLOCK`
       *  and list nothing. And it adds the any-date window ONLY WHEN NEITHER BOUND IS SET: a festival
       *  set is raised weeks ahead, so the server's seven-day default would show almost none of it.
       *  The window is added HERE, never written to the URL — so going back to Standard restores the
       *  server's default exactly, and a shared Standard link never carries a sentinel.
       *
       *  ⚠️ [amended P7B.1 §4] THE TYPE IS THE ROUTE'S (`fixedType`), NEVER A URL FILTER — so an
       *  unregistered type cannot reach the request at all.
       *
       *  ⚠️ [amended TYPE-FILTER] WITHOUT ONE, MANAGE ARRANGEMENTS ASKS FOR EVERY TYPE (`type=any`) — the
       *  user's new default — and "Order type" narrows it through `sessionQuery`, spread last below. The
       *  server's own default stays `none`, so no other caller moves.
       * ─────────────────────────────────────────────────────────────────────────────────
       */
      const typedAs = fixedType;
      const noDates = serverFilters.dateFrom === undefined && serverFilters.dateTo === undefined;
      const page = await api.get<ArrangementListPageDto>('/arrangements', {
        query: {
          page: params.page,
          limit: params.limit,
          sortBy: params.sortBy,
          sortOrder: params.sortOrder,
          ...(search ? { search } : {}),
          ...serverFilters,
          ...(typedAs
            ? { type: typedAs, ...(noDates ? ARRANGEMENT_ANY_DATE_WINDOW : {}) }
            : {
                type: ARRANGEMENT_TYPE_ANY,
                /*
                 * ⚠️ [DP2026] A DRAFT MADE FROM ITS OWN TYPE'S BOOKLET IS LISTED ON THAT TYPE'S PAGE ALONE until it is
                 * published — the user's rule for the 2026 booklet's drafts. A constant, never a URL or session key:
                 * it is what this list IS, not a filter anybody chose, so it is not in the cache key either.
                 */
                currentBookletDrafts: 'exclude',
                ...(kind ? { kind } : {}),
                ...(standingOrderId ? { standingOrderId } : {}),
              }),
          /*
           * ⚠️ [C5b §3] THE URL HOLDS A WORD AND THE SERVER IS SENT ITS STATUSES. `Published`
           * covers `PUBLISHED` and `ALLOCATION_IN_PROGRESS`, and the list schema reads a repeated
           * `?status=` as a list ([P6d]) — so the expansion is here, in one place, and a raw status
           * in an old shared link is read as the word it now belongs to.
           */
          ...(status ? { status: statusFilterQuery(status) } : {}),
          /*
           * ⚠️ [LIST-FILTERS] THE SESSION FILTER — a LIST, sent as repeated `divisionOrgUnitId` keys,
           * which the server reads as one division or several. `{}` with nothing applied.
           * [TYPE-FILTER] …and a narrowing "Order type", which replaces the `any` above.
           */
          ...sessionQuery,
        },
        signal,
      });
      /*
       * ⚠️ NESTED ONLY UNDER THE DATE SORT — see `lib/arrangementGroups.ts`.
       *
       * ⚠️ [C5b §6] AND TAGGED WITH THE TERM THAT PRODUCED IT. `keepPreviousData` keeps this page on
       * screen while the next term's request is in flight, so the empty state must read the term
       * the HINT belongs to — never the search box, which is already the next one.
       *
       * ⚠️ [LIST-FILTERS] …AND WITH WHETHER IT WAS FILTERED, for the same reason: the empty state must
       * say what THIS page's request carried, never what the store holds now — a cleared filter's page
       * is still the filtered one until its replacement lands. [TYPE-FILTER] The tag IS the session
       * query it sent, so the empty state can say WHICH filter hid everything.
       */
      return {
        ...page,
        items: groupRows(page.items, { nest: params.sortBy === 'paDate' }),
        ...(search ? { searchedFor: search } : {}),
        ...(Object.keys(sessionQuery).length > 0 ? { filteredBy: sessionQuery } : {}),
      };
    },
  });

  /*
   * ⚠️ [TYPED-BACK] WHICH LIST AN ORDER OPENED FROM HERE CAME FROM — this typed list's own address, its
   * page, sort and filters included, built from the TABLE's serialised state (`table.urlSearch`). Never
   * `useLocation().search`: nuqs writes the list's state shallowly, so the router's location never sees
   * a search typed here and every order would lead back to an unfiltered page one. `null` on Manage
   * Arrangements, which is an untyped order's way back already.
   */
  const origin = fixedType ? listOriginFor(fixedType, table.urlSearch) : null;

  /*
   * ─────────────────────────────────────────────────────────────────────────────────
   *  ⚠️ [LIST-FILTERS] APPLY AND CLEAR WRITE THE STORE AND RETURN TO PAGE ONE IN ONE HANDLER.
   *
   *  `table.setFilters({})` WRITES NO URL FILTER — nuqs walks an empty patch and writes nothing — and
   *  its page reset still lands, so the store's emit and the page write batch into ONE render and ONE
   *  request: the new divisions are never paired with page three of the old list. A change from
   *  ANOTHER tab has no handler here, and `useServerTable`'s page clamp is what pulls a page past the
   *  new last one back; a filtered result with no pages at all leaves the page where it was, under the
   *  filtered empty state, which is the right sentence for it.
   *
   *  ⚠️ APPLY WRITES ONLY A CHANGE. The dialog disables Apply on an unchanged draft; this holds the same
   *  rule for any other caller, so an unchanged Apply neither moves the page nor clears a selection.
   * ─────────────────────────────────────────────────────────────────────────────────
   */
  const applyListFilters = (next: ArrangementListFilters): void => {
    if (!sameListFilters(next, listFilters.filters)) {
      listFilters.apply(next);
      table.setFilters({});
    }
    setFiltersOpen(false);
  };
  const clearListFilters = (): void => {
    listFilters.clear();
    table.setFilters({});
    setFiltersOpen(false);
  };
  /* What the button's description says is applied — names once the tree has answered, a count until. */
  const listFiltersTitle = listFiltersSummary(
    listFilters.filters,
    divisionNames.isLoaded ? divisionNames.resolve : undefined,
    fixedType,
  );

  /*
   * ⚠️ `|| 'current'` — `useServerTable` STRIPS an empty filter value so it never reaches the
   * URL, which keeps a shared link clean; the page re-defaults on the way out, exactly as
   * `kind` does.
   */
  const show = table.params.filters.show || 'current';

  const kind = table.params.filters.kind || 'STANDARD';
  /* ⚠️ [amended P7B.1 §4] The route's type, or the untyped list — never a URL filter. */
  const typedTab = fixedType;
  /*
   * ⚠️ [C3a §2] A CLIENT-ONLY FILTER KEY. `view` says WHICH TABLE this page is showing; it is in
   * `filterKeys` so it lives in the URL and survives a refresh, and it is STRIPPED before the
   * arrangements query is sent — `GET /arrangements` has never heard of it and would strip it
   * silently, which is the failure mode [D4.4] records for exactly this shape.
   */
  const view = fixedType ? 'orders' : (table.params.filters.view ?? 'orders');
  /* ⚠️ [C5b §6] Neither bound sent: the SERVER's default window, which the placeholders state. */
  const noDateFilter = table.params.filters.dateFrom === undefined && table.params.filters.dateTo === undefined;
  /** The any-date window's bound is a sentinel and shows as EMPTY — see the pickers below. */
  const pickerDate = (value: string | undefined, sentinel: string) => (value === sentinel ? null : asPickerDate(value));
  const nested = table.params.sortBy === 'paDate';
  const selectedRows = table.tableProps.dataSource?.filter(
    (row) => row.rowKind === 'arrangement' && selectedKeys.includes(row.key),
  );
  /*
   * ⚠️ [P7B §6a] THE DISCARD SELECTION IS READ ONLY UNDER THE PARAMETERS IT WAS MADE UNDER — the page,
   * the sort, the search and every filter (the tab is a filter). See the state's own note.
   */
  const discardContext = JSON.stringify([
    table.params.page,
    table.params.limit,
    table.params.sortBy,
    table.params.sortOrder,
    table.params.search,
    table.params.filters,
    /*
     * ⚠️ [LIST-FILTERS] The session filter is a filter too — and another TAB can change it. [TYPE-FILTER]
     * What THIS list sends, so a Manage-only "Order type" change never drops a selection on a typed list.
     */
    sessionQuery,
  ]);
  const [discardContextSeen, setDiscardContextSeen] = useState(discardContext);
  if (discardContextSeen !== discardContext) {
    setDiscardContextSeen(discardContext);
    setDiscardPick([]);
  }
  const discardKeys = discardMode ? discardPick : [];
  const discardRows = (table.tableProps.dataSource ?? []).flatMap((row) =>
    row.rowKind === 'arrangement' && discardKeys.includes(row.key) ? [row] : [],
  );
  const anchorDate = selectedRows?.[0]?.rowKind === 'arrangement' ? selectedRows[0].paDate : null;

  /**
   * ⚠️ ONE PLACE THAT KNOWS A BAND IS NOT AN ARRANGEMENT. Every column but the first
   * collapses to `colSpan: 0` on a group row and otherwise renders against a narrowed
   * arrangement — so no column has to branch on the row kind, and adding a column later
   * cannot forget to.
   */
  const cell = (render: (row: ArrangementListItemDto) => ReactNode) => ({
    onCell: (row: GroupedRow) => (row.rowKind === 'group' ? { colSpan: 0 } : {}),
    render: (_value: unknown, row: GroupedRow): ReactNode => (row.rowKind === 'group' ? null : render(row)),
  });

  const invalidateAll = (): void => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.arrangements.all });
    setSelectedKeys([]);
  };

  const createGroup = useMutation({
    mutationFn: (body: { name: string; arrangementIds: string[] }) => api.post('/arrangement-groups', body),
    onSuccess: () => {
      setGroupOpen(false);
      void message.success('These orders are now listed together');
      invalidateAll();
    },
    onError: (error: ApiError) => void message.error(error.message),
  });

  const renameGroup = useMutation({
    mutationFn: (input: { id: string; name: string }) =>
      api.patch(`/arrangement-groups/${input.id}`, { name: input.name }),
    onSuccess: () => {
      setRenaming(undefined);
      invalidateAll();
    },
    onError: (error: ApiError) => void message.error(error.message),
  });

  const ungroup = useMutation({
    mutationFn: (id: string) => api.post(`/arrangement-groups/${id}/ungroup`, {}),
    onSuccess: invalidateAll,
    onError: (error: ApiError) => void message.error(error.message),
  });

  const removeMember = useMutation({
    mutationFn: (input: { groupId: string; arrangementId: string }) =>
      api.delete(`/arrangement-groups/${input.groupId}/members/${input.arrangementId}`),
    onSuccess: invalidateAll,
    onError: (error: ApiError) => void message.error(error.message),
  });

  /*
   * ⚠️ [C2b §4] IT NAVIGATES STRAIGHT INTO THE NEW DRAFT, `duplicate`'s shape on the detail
   * page. A corrigendum is a copy that needs editing — landing back on the list would leave
   * the operator to find a draft they did not name and cannot yet tell apart.
   */
  /*
   * ⚠️ [C3a §8] THE SAME ARCHIVE FLOW THE DETAIL PAGE CALLS — one dialog vocabulary, one
   * tier read, one pair of mutations. `invalidateAll` is this page's own, so the shared hook
   * never has to name a query key that belongs to somebody else's screen.
   */
  const archiving = useArchiveArrangement({ onDone: invalidateAll });

  /*
   * ⚠️ [P7B §6a] ONE REQUEST, EACH DRAFT ITS OWN TRANSACTION ON THE SERVER — always a 200 with an
   * outcome per draft, so a refusal never undoes a discard beside it. The refusals are SHOWN, under
   * the table, against the names the operator chose; a toast would vanish with the reasons in it.
   */
  const bulkDiscard = useMutation({
    mutationFn: (input: { ids: string[]; titles: Readonly<Record<string, string>> }) =>
      api.post<BulkDiscardResultDto>('/arrangements/bulk-discard', { arrangementIds: input.ids }),
    onSuccess: (result, input) => {
      setDiscardResult({ result, titles: input.titles });
      setDiscardPick([]);
      void queryClient.invalidateQueries({ queryKey: queryKeys.arrangements.all });
    },
    onError: (error: ApiError) => void message.error(error.message),
  });

  const confirmBulkDiscard = (): void => {
    const rows = discardRows;
    if (rows.length === 0) return;
    const titles = Object.fromEntries(rows.map((row) => [row.id, row.paNumber ?? row.displayTitle]));
    modal.confirm({
      title: DISCARD_MODE_COPY.confirmTitle(rows.length),
      content: (
        <div className="flex flex-col gap-1 text-sm" data-testid="discard-confirm-body">
          <span>{DISCARD_MODE_COPY.confirmBody}</span>
          <span className="text-muted-foreground">{DISCARD_MODE_COPY.confirmRestorable}</span>
        </div>
      ),
      okText: DISCARD_MODE_COPY.confirmOk(rows.length),
      okButtonProps: { danger: true, 'data-testid': 'discard-confirm-ok' },
      onOk: () => {
        bulkDiscard.mutate({ ids: rows.map((row) => row.id), titles });
      },
    });
  };

  const corrigendum = useMutation({
    mutationFn: (arrangementId: string) => api.post<ArrangementDto>(`/arrangements/${arrangementId}/corrigendum`, {}),
    onSuccess: (created) => {
      invalidateAll();
      /* ⚠️ [TYPED-BACK] Carrying the list, so the corrigendum's builder leads back to it. */
      void navigate(withListOrigin(`/arrangements/${created.id}/edit`, origin));
    },
    onError: (error: ApiError) => void message.error(error.message),
  });

  const openRename = (row: Extract<GroupedRow, { rowKind: 'group' }>): void => {
    setRenaming({ id: row.groupId, name: row.name });
  };

  const confirmUngroup = (row: Extract<GroupedRow, { rowKind: 'group' }>): void => {
    modal.confirm({
      title: `Ungroup "${row.name}"?`,
      /* ⚠️ It says what ungrouping does NOT do, which is everything anybody would worry about. */
      content: <span className="text-sm">{GROUP_COPY.ungroup}</span>,
      okText: 'Ungroup',
      onOk: () => {
        ungroup.mutate(row.groupId);
      },
    });
  };

  const allColumns: ColumnsType<GroupedRow> = [
    {
      key: 'reference',
      title: 'Arrangement name',
      sorter: true,
      /*
       * ⚠️ A BAND SPANS THE TABLE, so the group's own row carries `colSpan` here and every
       * other column answers `colSpan: 0` for it — that is AntD's idiom for a full-width row
       * and the reason each column below repeats the `rowKind === 'group'` clause.
       *
       * ⚠️ [amended UX-FINAL] IT SPANS THE TABLE **AS RENDERED**, NOT AS DECLARED. `columns`
       * is the VISIBLE set, which the Columns menu can shrink; `allColumns` is the declared
       * one. Spanning the declared count leaves a band running past the last visible column
       * and AntD then drops the overflow, so the band silently loses its right-hand edge the
       * first time an operator hides a column.
       *
       * ⚠️ [amended C2a §4f] RESTORED — `4099115` deleted this and left the `columns.length`
       * it explains, which is exactly the expression a later reader would "correct" to
       * `allColumns.length`.
       */
      onCell: (row) => (row.rowKind === 'group' ? { colSpan: columns.length } : {}),
      render: (_value, row) => {
        if (row.rowKind === 'group') {
          return (
            <div className="flex flex-wrap items-center gap-2" data-testid={`group-band-${row.groupId}`}>
              <span className="font-medium">{row.name}</span>
              <span className="text-xs text-muted-foreground">{row.paDate}</span>
              {isPartialGroup(row) && (
                <Tooltip title={GROUP_COPY.partialHint}>
                  <span className="text-xs text-muted-foreground" data-testid="group-partial">
                    {GROUP_COPY.partialOnPage(row.shownCount, row.memberCount)}
                  </span>
                </Tooltip>
              )}
              <Dropdown
                menu={{
                  items: [
                    {
                      key: 'rename',
                      label: 'Rename group',
                      disabled: !canWrite,
                      onClick: () => {
                        openRename(row);
                      },
                    },
                    {
                      key: 'ungroup',
                      label: 'Ungroup',
                      danger: true,
                      disabled: !canWrite,
                      onClick: () => {
                        confirmUngroup(row);
                      },
                    },
                  ],
                }}
                trigger={['click']}
              >
                <Button type="text" size="small" aria-label={`More actions for ${row.name}`}>
                  ⋯
                </Button>
              </Dropdown>
            </div>
          );
        }
        /*
         * ⚠️ [P3b.1] THE NAME IS THE WAY IN — DESIGN_LANGUAGE §4, at last.
         *
         * This page was recorded there as THE ONE EXCEPTION, with a whole-row click. That
         * exception survived because it cost nothing; it now costs something. A checkbox, an
         * expand chevron and a row-wide navigation in one row means `stopPropagation` at
         * three sites and at every control added later — and forgetting it is SILENT: the
         * row navigates away and the operator loses their selection. The original reason for
         * the handler (routing two ways, DRAFT → /edit) survives inside `to`, and gains
         * ⌘-click, copy-link and a visible destination a button-shaped row never had.
         */
        /* [TYPE-FILTER] The row's type, on the list that mixes types — `null` untyped, or on a typed list. */
        const typeChip = fixedType === undefined ? arrangementTypeLabel(row.type) : null;
        return (
          /*
           * ⚠️ [D4.4 §7a] THE NAME IS CAPPED. `arrangementTitle.util.ts` derives a title from
           * every place a card names, so a three-duty-point order produces something like
           * "Esplanade → Park Street → Maidan on 15 Aug 2026" — which ran straight into the
           * Date column beside it. See `NameCell`'s header for why this is a narrow
           * exception to §8's "cells never truncate" rather than a reversal of it: the name
           * is the one cell whose full text is reachable another way, because it is the
           * link to the record.
           */
          <NameCell
            name={row.displayTitle}
            maxWidth="26rem"
            /* ⚠️ [TYPED-BACK] Carrying the typed list it was opened from; unchanged on Manage Arrangements. */
            to={withListOrigin(`/arrangements/${row.id}${row.status === 'DRAFT' ? '/edit' : ''}`, origin)}
            secondary={
              row.templateSource ? (
                <>
                  <span className="font-mono">{row.paNumber ?? row.reference}</span>
                  {/* [P7B §5] Where a typed order came from — keys only, so no template is read. */}
                  <span data-testid="row-template-source"> · {templateSourceLabel(row.templateSource)}</span>
                </>
              ) : (
                <span className="font-mono">{row.paNumber ?? row.reference}</span>
              )
            }
            /*
             * ⚠️ [C5c §10 · amended P7A §2] THE RAISER'S LEVEL BESIDE THE NAME, OUTSIDE THE LINK —
             * `HQ` · `DRO` · `Unit`. The server decides it from the RAISER's position; this row only
             * draws it. `undefined` when the server sends `null`, so such a row's DOM is exactly what
             * an unflagged row's always was.
             */
            badge={
              row.inChargeUnnamed === true || typeChip !== null ? (
                /*
                 * ⚠️ [P7B §5] A TYPED DRAFT PUBLISH WILL HOLD BACK — beside the raiser pill, in the
                 * same slot. `inChargeUnnamed` is sent on a typed draft only, so every untyped row's
                 * badge is exactly what it was.
                 *
                 * ⚠️ [TYPE-FILTER] AND A TYPED ROW ON MANAGE ARRANGEMENTS SAYS ITS TYPE — the record page's
                 * own chip — because that list now mixes every type. Never on a typed list, where every row
                 * is that type.
                 */
                <span className="flex items-center gap-1.5">
                  {row.raisedAtLevel === null ? null : <RaiserPill level={row.raisedAtLevel} />}
                  {typeChip === null ? null : (
                    <span
                      className="whitespace-nowrap rounded-full border border-border px-2 py-0.5 text-xs text-foreground"
                      data-testid="row-type"
                    >
                      {typeChip}
                    </span>
                  )}
                  {row.inChargeUnnamed === true ? (
                    <StatusPill tone="warning" data-testid="row-incharge-unnamed">
                      {IN_CHARGE_UNNAMED_LABEL}
                    </StatusPill>
                  ) : null}
                </span>
              ) : row.raisedAtLevel === null ? undefined : (
                <RaiserPill level={row.raisedAtLevel} />
              )
            }
          />
        );
      },
    },
    /*
     * ⚠️ [C5c §9] THE DAY OF THE LAST UPDATE, SO THE DEFAULT SORT'S GROUPING IS LEGIBLE. It is the
     * IST day — `istDutyDate` over the instant, never `.slice(0, 10)` on the ISO string, which is
     * the UTC day and is yesterday for five and a half hours of every one ([IST-FIX]).
     */
    {
      key: 'updatedDay',
      title: 'Updated',
      width: 120,
      ...cell((row) => <span>{readableDate(istDutyDate(row.updatedAt)) ?? istDutyDate(row.updatedAt)}</span>),
    },
    /*
     * ─────────────────────────────────────────────────────────────────────────────────
     *  ⚠️ [amended C2a §4a] THE OPERATIONAL SPAN, BESIDE THE ADMINISTRATIVE `Order Date`.
     *
     *  [D4]'s rule, made visible: `pa_date` says "the order was dated X" and
     *  `first_duty_date`/`last_duty_date` say "there is duty on X". An order dated the 14th
     *  that runs 15–17 August has no single date, and until this column the list showed only
     *  the first of those two answers.
     *
     *  ⚠️ ITS KEY WAS `slots`, WHICH THE SHIFT-COUNT COLUMN BELOW ALREADY OWNS — and so was
     *  its TITLE. Two columns sharing a key share one `useColumnVisibility` entry, so hiding
     *  one hid both, and React saw two siblings with the same key. Two columns sharing a
     *  title made the Columns menu name the same thing twice.
     *
     *  ⚠️ AND IT FORMATS THROUGH `readableDate`, NOT `toLocaleDateString`. That helper is IST-
     *  zoned (divergence #73); the inlined `new Date(...).toLocaleDateString('en-GB')` it
     *  replaces resolves in the RUNTIME's zone, which is the exact way a business date gets
     *  written in one zone and read in another (§7).
     * ─────────────────────────────────────────────────────────────────────────────────
     */
    {
      key: 'dutyDates',
      title: 'Duty dates',
      width: 180,
      ...cell((row) => {
        const start = row.firstDutyDate;
        if (!start) return <span className="text-subtle">—</span>;

        const end = row.lastDutyDate ?? start;
        const from = readableDate(start) ?? start;
        return <span>{start === end ? from : `${from} – ${readableDate(end) ?? end}`}</span>;
      }),
    },
    /*
     * ⚠️ [C3c §8b] `Order Date` AND `Duties` ARE DELETED, NOT DEFAULT-HIDDEN.
     *
     * `useColumnVisibility` stores the HIDDEN set and has no `defaultHidden` concept — its own
     * header argues for the URL and against a second store — so teaching it one would be the
     * first change to that hook across eleven consumers, for a shape one page wants. §9's rule
     * then applies: a column nobody renders is deleted rather than flagged.
     *
     * ⚠️ A STALE STORED PREFERENCE OR SHARED `?hide=paDate.slots` LINK IS ALREADY HANDLED —
     * `hiddenKeys` filters against the LIVE column set, so a key that no longer exists is
     * ignored rather than breaking the menu.
     *
     * ⚠️ AND `paDate`'s SORTER WENT WITH IT, WHICH IS WHY THE SORT CONTROL EXISTS. It carried the
     * only route to the order-date sort, and `arrangementGroups.ts` nests only under that sort —
     * so deleting this column without replacing the control would have removed corrigendum
     * families from the UI entirely.
     */
    {
      key: 'divisions',
      title: 'Divisions',
      width: 160,
      ...cell((row) =>
        row.divisions.length === 0 ? (
          <span className="text-subtle">—</span>
        ) : (
          <span className="truncate">{row.divisions.map((division) => division.shortCode).join(', ')}</span>
        ),
      ),
    },
    {
      key: 'status',
      /*
       * ⚠️ [C5c follow-up] NOT PINNED, AND BEFORE Filled. The pin rule is for an ACTION ("an action a
       * scroll can hide is an action that is not there"), and Status is a fact about the row — pinned
       * beside the ⋯ menu it read as part of the actions and split the two facts it belongs with.
       * Lifecycle first, then the fill answer, is the reading order the row now has; only the ⋯
       * column stays pinned.
       */
      title: 'Status',
      width: 140,

      // A dot with a label, never a wall of filled badges — that is what makes the one
      // pill on a detail record worth reading.
      /*
       * ⚠️ [C5b §3] THE LIFECYCLE WORD, WITH THE ARCHIVE AXIS — `orderStatusWord`. This column read
       * `status` alone, so an archived draft said `Draft` in the Archive view and a filed order
       * said `Closed`; and `ALLOCATION_IN_PROGRESS` said `Partially Filled` beside the Filled
       * column's own, different, answer.
       */
      ...cell((row) => {
        const word = orderStatusWord(row);
        return <StatusDot tone={word.tone} label={word.label} />;
      }),
    },
    /**
     * ─────────────────────────────────────────────────────────────────────────────────
     *  ⚠️ [C5 §8c] `N of M named`, THE SAME COLUMN THE DUTY POINT ROWS NOW CARRY.
     *
     *  Both numbers have been on `ArrangementListItemDto` since [P4] — `listArrangements` reads
     *  them from `arrangementFillCounts`, the roll-up this list already pays for — and no column
     *  rendered either. So this is a COLUMN, not an API change: no request moves and no query
     *  widens.
     *
     *  ⚠️ THE WORD COMES FROM `fillStateLabel`, WHICH IS §3b's WHOLE POINT ON THIS SCREEN. An
     *  order every division closed short at `0 of 12` used to read `Complete` here; it reads
     *  `Closed short` now, beside the numbers that say why that is not the same as filled.
     *
     *  ⚠️ [amended C5b §3] THROUGH `fillSummary`, the one spelling of this cell on every screen —
     *  and its tone is `fillTone`, so an order asking for nobody reads `Nobody named` in neutral
     *  rather than in the red of an unanswered request.
     *
     *  ⚠️ IT IS NOT SORTABLE. §7 ships an index with the query that needs one, and there is no
     *  index behind a derived roll-up — a `sortBy` value with nothing behind it is
     *  `listArrangements`' own blocking-sort hazard, which [C3a §2] records.
     * ─────────────────────────────────────────────────────────────────────────────────
     */
    {
      key: 'filled',
      title: 'Filled',
      width: 170,
      ...cell((row) => {
        const filled = fillSummary(row);
        return <StatusDot tone={filled.tone} label={filled.label} />;
      }),
    },
    {
      key: 'row-actions',
      // [P4-UX] Pinned: an action a scroll can hide is an action that is not there.
      fixed: 'right' as const,
      title: '',
      width: 60,

      align: 'right',
      ...cell((row) => (
        <Dropdown
          menu={{
            items: [
              /*
               * ⚠️ [TYPED-BACK] A TYPED DRAFT'S RECORD PAGE GETS A DOOR OF ITS OWN. The draft's name opens
               * its builder, and a builder opened straight from this list goes straight back to it — so
               * without this entry the draft's record page (the only home of "Delete permanently", the
               * revert notice, Print order and the template link) is reached from here by nothing at all.
               *
               * ⚠️ A REAL `<a>` — `NameCell`'s rule: a door can be ⌘-clicked and copied; antd stretches an
               * anchor over its whole menu item. It carries the list like every other way in.
               *
               * ⚠️ ONLY ON A TYPED LIST (`origin`) AND ONLY ON A DRAFT: an untyped draft's builder still
               * goes back to its record, and an issued row's name already opens the record.
               */
              origin !== null && row.status === 'DRAFT'
                ? {
                    key: 'open-record',
                    label: (
                      <Link to={withListOrigin(`/arrangements/${row.id}`, origin)}>{TYPED_TAB_COPY.openRecord}</Link>
                    ),
                  }
                : null,
              /*
               * ⚠️ [C2b §4] A CORRIGENDUM IS ISSUED AGAINST AN ISSUED ORDER, so the entry is
               * offered on exactly those — a draft is edited, not corrected.
               *
               * ⚠️ AND NEVER ON A CORRIGENDUM ITSELF. `attachCorrigendum` refuses one on the
               * server (§4h), and an entry that exists to be refused is worse than none:
               * `row.group.role` is what the wire already says, so this asks the same
               * question the server will rather than a second one derived from the shape.
               */
              ARRANGEMENT_STATUSES_ISSUED.includes(row.status as (typeof ARRANGEMENT_STATUSES_ISSUED)[number]) &&
              row.group?.role !== 'CORRIGENDUM'
                ? {
                    key: 'corrigendum',
                    label: 'Issue a corrigendum',
                    disabled: !canWrite || !mayRaise || corrigendum.isPending,
                    onClick: () => {
                      corrigendum.mutate(row.id);
                    },
                  }
                : null,
              /*
               * ⚠️ A CORRIGENDUM BAND IS NOT UNGROUPABLE FROM HERE. `detachArchivedMember`
               * early-returns on a corrigendum family and `removeMember` addresses the manual
               * group endpoints, so offering it would post at a group that refuses it.
               */
              row.group && row.group.kind === 'MANUAL'
                ? {
                    key: 'remove',
                    label: 'Remove from group',
                    disabled: !canWrite,
                    onClick: () => {
                      removeMember.mutate({ groupId: row.group?.id ?? '', arrangementId: row.id });
                    },
                  }
                : null,
              /*
               * ─────────────────────────────────────────────────────────────────────────
               *  ⚠️ [C3a §8] ONE ACT, TWO NAMES, DECIDED BY THE ROW'S OWN STATUS.
               *
               *  There is exactly ONE endpoint — `POST /arrangements/:id/archive` — and this
               *  repo already has two words for it: `Discard` is the builder's word on a DRAFT
               *  (`DISCARD_COPY.action`) and `Archive` is [C1e §6b]'s word for the AXIS on an
               *  issued order. So the LABEL follows the record and the DIALOG names the act,
               *  which is that pass's own rule rather than a new one.
               *
               *  ⚠️ THE BRIEF CALLED THIS A REGRESSION AND IT IS NOT. `git log -S` over this
               *  file finds no commit that ever removed a Discard, and the two that touch
               *  "Archive" are the `show` filter and this. What `a66d8e1` deleted was a
               *  permanently-disabled `Not in a group` placeholder. This is a FEATURE.
               *
               *  ⚠️ AND IT FIXES A LIVE DEFECT ON THE WAY PAST: a DRAFT that is not in a group
               *  matched neither entry above, so `⋯` opened a dropdown with ZERO items — and
               *  `e2e/fixtures/overlays.ts` opens exactly that control at four viewports, so
               *  that sweep has been passing over an empty overlay. Every row now carries one.
               *
               *  ⚠️ THE TIER IS RESOLVED ON CLICK, NOT PER ROW. `GET /:id/delete-impact` is four
               *  clock comparisons plus a cascade read PER ARRANGEMENT; eagerly it would be one
               *  request per row on every render and every sort. It also flips on the CLOCK WITH
               *  NO WRITE ([C1e §5]), so an eagerly-fetched tier is stale by the time it is
               *  clicked — and a stale `FILING` shown as `CANCELLATION` is the wrong dialog on a
               *  destructive act. The new VIEW page fetches it eagerly because it shows ONE
               *  record; that asymmetry is correct and is stated at both sites.
               * ─────────────────────────────────────────────────────────────────────────
               */
              row.archivedAt === null
                ? {
                    key: 'archive',
                    label: row.status === 'DRAFT' ? ARCHIVE_COPY.menuDiscard : ARCHIVE_COPY.menuArchive,
                    danger: true,
                    disabled: !canWrite || !canDelete || archiving.isArchiving,
                    onClick: () => {
                      void archiving.confirmArchive(row);
                    },
                  }
                : null,
              /*
               * ⚠️ RESTORE IS HERE BECAUSE `Show: Archived` HAD NO ACTION ON ANY ROW. The list
               * has offered an archived view since [LIFECYCLE] and the only way back was the
               * order's own detail page — so a list that can put an order away and not get it
               * back is the asymmetry this closes. The gate is `archivedTierOf` + `mayArchiveTier`,
               * the SAME decision the detail page makes, from fields the row already carries
               * (`status`, `archivedAt`, `archivedFromStatus`) — so it costs no payload.
               */
              row.archivedAt !== null && archiving.mayRestore(row)
                ? {
                    key: 'restore',
                    label: ARCHIVE_COPY.menuRestore,
                    disabled: !canDelete || archiving.isRestoring,
                    onClick: () => {
                      archiving.restore(row);
                    },
                  }
                : null,
            ].filter((item) => item !== null),
          }}
          trigger={['click']}
        >
          <Button type="text" size="small" aria-label={`More actions for ${row.displayTitle}`}>
            ⋯
          </Button>
        </Dropdown>
      )),
    },
  ];

  /*
   * ⚠️ THE MENU GETS THE UNFILTERED ARRAY; THE TABLE GETS THE FILTERED ONE.
   */
  const columnKeys = columnKeySet(allColumns);
  const columnVisibility = useColumnVisibility({ ...columnKeys, storageKey: 'arrangements' });
  const columns = allColumns.filter((column) => columnVisibility.isVisible(String(column.key)));

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        icon={CalendarClock}
        title={fixedType ? ARRANGEMENT_TYPES[fixedType].label : ARRANGEMENTS_COPY.title}
        /* ⚠️ [PRE-P6.2] NO SUBTITLE. The three operations screens carry a title and nothing
           else — a standing description under a heading is read once and then occupies a
           line of every visit for ever. */
        actions={
          /*
           * ⚠️ [C3a §2] THE ACTION CLUSTER SWAPS WITH THE VIEW, IT DOES NOT ACCUMULATE.
           *
           * All three order actions write ARRANGEMENTS, and none of them means anything while
           * the library is on screen — `Start from a previous one` opens a copy picker over a
           * list this view is not showing. A fourth button beside them would read as a fourth
           * thing to do with an order.
           *
           * ⚠️ IT IS `arrangement.write` AND NOT A NEW CAPABILITY, and NOT
           * `assertMayRaiseArrangement`: a unit may not RAISE an order because that tasks its
           * superior, and filing a copy of one somebody else raised tasks nobody. Stated here
           * and at the route, because the two read as inconsistent otherwise.
           */
          <>
            {/*
              ⚠️ [LIST-FILTERS] "ADJUST FILTERS" IS IN THE HEADER, AND IT IS FIRST — on both lists:
              Adjust filters, Upload a PA, Start from a previous one, New arrangement… on Manage
              Arrangements, and Adjust filters, Templates, Publish drafts… on a typed list.

              ⚠️ IN THE HEADER, NOT THE FILTER ROW, at the user's direction: it is not one more filter
              beside Show and Status but the door to a set of them that follows the operator off this page
              — to the other order list and into every tab — and a filter row is the page's own state.

              ⚠️ LEFTMOST, FOR TWO REASONS. It is the one control here whose WIDTH changes with its state
              ("Adjust filters" to "Filters applied · 2"), and leftmost in a right-aligned cluster its
              growth moves nothing else — New arrangement and the Columns gear never shift under the
              pointer. And it is read, and tabbed to, before the actions it qualifies. `PageHeader`'s
              "primary action last" orders ACTIONS; this writes no order, the Columns menu's argument.

              ⚠️ HIDDEN OVER UPLOADS, like every other order control: an uploaded PA is never a row of this
              table, so a filter over the library would filter nothing on screen.

              ⚠️ THE OFFER GATE IS `mayFilterByDivision` — a unit's scoped tree holds no division, so a unit
              seat would be offered an empty picker — OR A FILTER ALREADY APPLIED, so whoever holds one can
              always clear it. An offer only: the server confines the request to the caller's scope
              whatever divisions it names.

              ⚠️ [TYPE-FILTER] …ON A TYPED LIST. Manage Arrangements always offers it, because "Order type"
              is open to every role that can open the page; the dialog itself offers Division by the rule
              above (`ArrangementFiltersModal`'s `offered`).

              ⚠️ [DP2026] AND NOW THE TYPED LIST ALWAYS OFFERS IT TOO, for "Booklet" — open to every role that
              can open the page, exactly as Order type is on the other list. Division inside it keeps its rule.
            */}
            {view === 'library' ? null : (
              <ListFiltersButton
                activeCount={listFilters.activeCount}
                summary={listFiltersTitle}
                onOpen={() => {
                  setFiltersOpen(true);
                }}
              />
            )}
            {/*
              ⚠️ [amended C3c §8c] ONE BUTTON NOW SURVIVES THE SWAP, AND IT IS THE ONE THAT IS NOT
              ABOUT AN ORDER — a deliberate REVERSAL of the rule above, for exactly one control.

              That rule is right about the other two: `Start from a previous one` opens a copy
              picker over a list the library is not showing. Filing a scanned PA is not an act on
              the order list at all — it is the way PAPER enters the system, and an operator
              holding paper is on whichever tab they happened to leave open. Making them find the
              Uploads tab first is a step that exists only because of how the screen is built.

              ⚠️ "STYLED DISTINCTLY" IS `primary` ON ITS OWN TAB AND `default` BESIDE THE ORDERS'
              ACTIONS, so it never competes with `New arrangement` for the eye — which is what
              that phrase has to mean once a button appears beside two others.
            */}
            {/* [P7B.1 §4] Not on a typed list: a typed order is created from a template, never filed as paper. */}
            {fixedType ? null : (
              <Tooltip title={canWrite ? undefined : READ_ONLY_TOOLTIP}>
                <Button
                  type={view === 'library' ? 'primary' : 'default'}
                  disabled={!canWrite}
                  data-testid="upload-order"
                  onClick={() => {
                    setUploadOpen(true);
                  }}
                >
                  {LIBRARY_COPY.add}
                </Button>
              </Tooltip>
            )}
            {view === 'library' ? null : (
              <>
                {/*
                  ⚠️ [P7B §5] THE TYPED TAB'S WORK IS THE TEMPLATES, SO ITS ACTIONS ARE THEIRS. A typed
                  order is raised from a template, never from the blank form or by copying an untyped
                  one, so `New arrangement` and `Start from a previous one` give way to `Templates` and
                  `Publish drafts` — offered to a template author only (`mayUseTemplates`, the route's
                  own seat gate). Every other role reads the tab and is offered nothing to write.
                */}
                {typedTab ? (
                  templateAuthor ? (
                    <>
                      <Button data-testid="typed-templates" onClick={() => void navigate('/templates')}>
                        {TYPED_TAB_COPY.templates}
                      </Button>
                      <Button
                        type="primary"
                        data-testid="typed-publish"
                        onClick={() => void navigate(`/templates?publish=${typedTab}`)}
                      >
                        {TYPED_TAB_COPY.publish}
                      </Button>
                    </>
                  ) : null
                ) : (
                  <>
                    {/*
              ⚠️ [amended C2a §3b] GATED ON `mayRaise` TOO, AND IT WAS THE OPEN DOOR.

              It read `canWrite` alone, so a nodal whose "New arrangement" button was disabled
              with "Arrangements are raised by the CP office" could reach the identical outcome
              one button to the left — `POST /arrangements/:id/duplicate` returns a new DRAFT.
              A gate applied to one door of three is not a gate.
            */}
                    <Tooltip
                      title={mayRaise ? (canWrite ? undefined : READ_ONLY_TOOLTIP) : ARRANGEMENTS_COPY.mayNotRaise}
                    >
                      <Button disabled={!canWrite || !mayRaise} onClick={() => setCopyOpen(true)}>
                        Start from a previous one
                      </Button>
                    </Tooltip>
                    {/*
              ⚠️ [UX1 §8] IT IS GATED ON THE SAME PREDICATE THE ROUTE AND THE SERVER USE, AND IT
              IS DISABLED RATHER THAN HIDDEN.

              ⚠️ THIS CLOSES A PRE-EXISTING HOLE. Before this pass a UNIT nodal saw an ENABLED
              primary button that navigated straight to `/forbidden` — `canWrite` is
              `arrangement.write`, which a station nodal holds, and nothing here read the node.

              Hidden is refused twice: [C1c §7a] found that a hidden entry point still left the
              form reachable, so absence buys nothing; and the absence of a page's PRIMARY action
              reads as a broken page rather than as a policy. The route and the API are the gate
              — this is courtesy, like everything else in `accessRole.ts`.
            */}
                    <Tooltip
                      title={mayRaise ? (canWrite ? undefined : READ_ONLY_TOOLTIP) : ARRANGEMENTS_COPY.mayNotRaise}
                    >
                      <Button
                        type="primary"
                        disabled={!canWrite || !mayRaise}
                        onClick={() => void navigate('/arrangements/new')}
                      >
                        New arrangement
                      </Button>
                    </Tooltip>
                  </>
                )}
                {/*
                  ⚠️ [P7B §6a] THE HOLDER'S DISCARD MODE, ON BOTH ORDER TABS — a toggle, so the
                  checkboxes say which question they answer. Absent for everybody else.
                */}
                {mayDiscardMany ? (
                  <Button
                    data-testid="discard-mode"
                    aria-pressed={discardMode}
                    onClick={() => {
                      setDiscardMode((current) => !current);
                      setDiscardPick([]);
                      setSelectedKeys([]);
                    }}
                  >
                    {discardMode ? DISCARD_MODE_COPY.stop : DISCARD_MODE_COPY.start}
                  </Button>
                ) : null}
                {/*
                  ⚠️ [C3c.2] THE COLUMNS CONTROL MOVED UP HERE, WHICH REVERSES THIS PAGE'S OWN
                  STATED REASON FOR PUTTING IT IN THE FILTER ROW.

                  That reason read: "A view control, so it sits IN the filter row — the same
                  argument this page already makes for its Segmented." The argument was sound and
                  the OUTCOME was not: this page carries a Segmented, Show, Search, Status, Sort
                  and two date pickers, so the row wrapped and the one control that is NOT a
                  filter was the thing taking a whole second line to itself. The Segmented keeps
                  the old argument because it genuinely IS a filter — it sets `view`.

                  ⚠️ IT IS AFTER THE PRIMARY, WHICH `PageHeader` SAYS ACTIONS ARE NOT. That note
                  orders ACTIONS — things that write an arrangement — and this writes nothing: it
                  is a per-operator view preference stored in the URL and `localStorage`. Placing
                  it before `New arrangement` would put a gear between two buttons that do belong
                  in a sequence, which reads worse than a trailing utility.

                  ⚠️ AND IT INHERITS THE LIBRARY GUARD BY SITTING INSIDE THIS FRAGMENT. It is
                  driven by `allColumns`, which are the ORDERS table's columns — the library
                  renders its own table, so a Columns control over it would name columns that are
                  not on screen. That is the same sentence the filter block above makes, and it
                  keeps being true only because this is inside the `view !== 'library'` branch —
                  the ELSE arm of the ternary above, which is the one the control is actually in.
                */}
                <ColumnsMenu
                  variant="icon"
                  columns={allColumns}
                  visibility={columnVisibility}
                  locked={columnKeys.locked}
                />
              </>
            )}
          </>
        }
        filters={
          <>
            {/*
              ⚠️ [P3b.1] THE SECOND TAB IS ONE FILTER VALUE AND NOTHING ELSE. `kind` shipped
              at P3a with its index and its server-side default; this is the control that
              sets it.

              ⚠️ [amended C3a §2] AND THE THIRD IS NOT A `kind` AT ALL — IT IS A VIEW.
              `Uploads` swaps the whole table, its columns and its query, so it binds a new
              `view` filter key rather than `kind`. That keeps `?kind=ROUND_THE_CLOCK` links
              anybody has already shared working, and `view` is an opaque string, which is
              exactly what `filterKeys` is documented to carry — so `useServerTable` gains a
              consumer with no hook change. The `queryFn` strips `view` before sending, so a
              client-only key never reaches the server.

              ⚠️ `LIBRARY` IS NOT AN `ArrangementKind` and must never enter
              `ARRANGEMENT_KIND_LABEL`. That is how a value the server's `z.enum` refuses ends
              up in `?kind=` and 422s the list on every keystroke.

              ⚠️ IT IS STILL A `Segmented` AND NOT `Tabs`, AND THIS FILE'S OWN TEST FOR TABS IS
              WHY: they earn their place in `OrgUnitDetail` only because their labels carry live
              counts, which here would cost a second query per tab. A `Tabs` strip in this flex
              row would also read as a second tab bar beside `Show` — the objection that control
              already records one place to the right. What has changed is that this is no longer
              a TWO-VALUE switch, so that half of the old sentence is gone rather than left
              standing for the next reader to add a fourth `kind` under.

              It sits IN the filter row rather than above it because it IS a filter, and
              `PageHeader`'s filter row is a flex line these already sit on. `|| 'STANDARD'`
              because `useServerTable` strips empty values while the server defaults the
              same way — so the right tab is selected on first paint with no hook change.
            */}
            {/*
              ⚠️ [amended P7B.1 §4] THE TYPED OPTION P7B ADDED HERE IS GONE — a typed arrangement is its
              own sidebar row and its own route (`/pa/:typeKey`), which renders this list with
              `fixedType` and no `Segmented` at all. Three values remain, and Standard still clears
              `kind` rather than writing the server's own default, so a return to Standard is the same
              URL — and the same request — as a first visit.
            */}
            {fixedType ? null : (
              <Segmented
                className="self-end"
                data-testid="arrangement-kind"
                value={view === 'library' ? 'LIBRARY' : kind}
                onChange={(value) => {
                  const next = String(value);
                  setSelectedKeys([]);
                  setDiscardPick([]);
                  if (next === 'LIBRARY') {
                    table.setFilter('view', 'library');
                    return;
                  }
                  table.setFilters({ kind: next === 'STANDARD' ? undefined : next, view: undefined });
                }}
                options={[
                  { value: 'STANDARD', label: ARRANGEMENT_KIND_LABEL.STANDARD },
                  { value: 'ROUND_THE_CLOCK', label: ARRANGEMENT_KIND_LABEL.ROUND_THE_CLOCK },
                  { value: 'LIBRARY', label: LIBRARY_COPY.tab },
                ]}
              />
            )}
            {/*
              ⚠️ [C3a §2] EVERY FILTER BELOW THE SWITCH IS ABOUT AN ARRANGEMENT, SO NONE OF THEM
              RENDERS OVER THE LIBRARY. `Show` is an archive axis the library has no column for,
              `Status` is a `ARRANGEMENT_STATUSES` value, the two dates read `first_duty_date`
              and `last_duty_date`, the search is `table.searchInput` — which belongs to the
              orders table this view is not rendering. A control that filters nothing on screen is
              worse than an absent one: it reads as a filter that has stopped working.

              ⚠️ [amended C3c.2] `ColumnsMenu` WAS THE FIFTH ITEM IN THIS LIST AND HAS MOVED TO THE
              ACTION CLUSTER. The rule is unchanged and so is its guard — it sits in the same
              `view !== 'library'` branch up there (written there as the ELSE arm of a ternary, and
              here as this `&&`), for the same reason it was named in this list.
            */}
            {view !== 'library' && (
              <>
                {/*
              ⚠️ [LIFECYCLE] TWO AXES, TWO CONTROLS — never one mixed dropdown. `Show` says
              whether a row is in circulation; `Status` says where it is in its own life.
              Collapsing them would make "Archived" look like a peer of "Draft", which is the
              confusion the archive-is-an-axis design exists to prevent.

              ⚠️ A `Select`, NOT a `Segmented` — three values stop it being a switch, and here
              it would sit beside a Segmented and read as a second tab bar. No `allowClear`:
              "Current" is a value, not the absence of a filter.
            */}
                <FilterField label="Show" className="w-44">
                  <Select
                    className="w-full"
                    value={show}
                    onChange={(value: string) => table.setFilter('show', value === 'current' ? undefined : value)}
                    options={[
                      { value: 'current', label: 'Current' },
                      { value: 'archived', label: 'Archived' },
                      { value: 'all', label: 'All' },
                    ]}
                    data-testid="show-filter"
                  />
                </FilterField>
                <FilterField label="Search" className={ARRANGEMENT_SEARCH_FIELD_CLASS}>
                  <Input.Search
                    allowClear
                    placeholder={ARRANGEMENT_SEARCH_READS}
                    value={table.searchInput}
                    onChange={(event) => table.onSearchChange(event.target.value)}
                  />
                </FilterField>
                {/*
              ⚠️ [D4 §6] SUPER_ADMIN ONLY, AND IT IS A ROLE COMPARISON RATHER THAN A
              CAPABILITY. `accessRole.ts`'s header is explicit that none of this is
              enforcement — the server still serves `?status=` to everyone — so inventing an
              `arrangement.filterStatus` capability would imply an authorisation decision
              that does not exist.

              ⚠️ AND `'status'` STAYS IN `filterKeys`. Hiding the CONTROL must not break a
              link somebody has already shared with `?status=` in it.
            */}
                {officer?.accessRole === 'SUPER_ADMIN' && (
                  <FilterField label="Status" className="w-40">
                    <Select
                      allowClear
                      placeholder="Any"
                      value={statusFilterWord(table.params.filters.status)}
                      onChange={(value?: string) => table.setFilter('status', value)}
                      /* ⚠️ [C5b §3] WORDS, so no two options read alike — `queryFn` expands one. */
                      options={statusFilterOptions()}
                    />
                  </FilterField>
                )}
                {/*
                  ⚠️ [C3c §8a/§8b] THE SORT CONTROL, AND IT EXISTS BECAUSE §8b DELETED THE ONLY
                  ROUTE TO THE ORDER-DATE SORT.

                  `arrangementGroups.ts` nests a group's members ONLY while the list is sorted by
                  `paDate`, because that is the one sort under which they are adjacent — its own
                  header says "under any other sort, adjacency is gone and nesting is nonsense".
                  §8a moves the default off that sort and §8b removes the `Order Date` column,
                  which carried its `sorter`. Between them, corrigendum families and manual groups
                  would have stopped being visible ANYWHERE in the product.

                  ⚠️ AND `dutyDates` DOES NOT GET A SORTER INSTEAD. Members of a group share a
                  `pa_date`, not a duty date, so under a duty-date sort they are not adjacent and
                  a group would render two or three times with one member each — which
                  `arrangementGroups.ts` refuses by name.
                */}
                <FilterField label="Sort" className="w-44">
                  <Select
                    value={table.params.sortBy}
                    data-testid="arrangement-sort"
                    onChange={(next: (typeof ARRANGEMENT_SORT_FIELDS)[number]) => {
                      table.setSort(next, next === 'paDate' || next === 'updatedDay' ? 'desc' : 'asc');
                    }}
                    options={[
                      { value: 'updatedDay', label: 'Last updated (newest first)' },
                      { value: 'firstDutyDate', label: 'Duty date (soonest first)' },
                      { value: 'paDate', label: 'Order date (newest first)' },
                      { value: 'reference', label: 'Name' },
                    ]}
                  />
                </FilterField>
                {/*
                  ⚠️ [C5b §6] CONTROLLED NOW — and "Show matches" is why it had to be. The pickers had an
                  `onChange` and no `value` ([D4.5 §3b]'s inbox defect, still live here), so a reset
                  that widened the dates to the any-date window left the old dates painted in the
                  boxes above a list that no longer used them.

                  ⚠️ THE ANY-DATE WINDOW IS A SENTINEL, NOT A DATE ANYBODY CHOSE, so a bound equal to
                  it shows as EMPTY and says "Any date". With NEITHER bound sent the placeholders
                  state the SERVER's default window instead — today plus seven — which is the
                  inbox's rule: the default is stated where the dates would be, never written into
                  the URL, and a single bound really does leave the other side open.
                */}
                <FilterField label="From" className="w-36">
                  <DatePicker
                    format="dd LLL yyyy"
                    /* ⚠️ [P7B §5] The typed tab sends the any-date window when no bound is set, so its
                       empty pickers say so — the server's default window is not the one in force. */
                    placeholder={noDateFilter && !typedTab ? SEARCH_HINT_COPY.fromDefault : SEARCH_HINT_COPY.anyDate}
                    value={pickerDate(table.params.filters.dateFrom, ARRANGEMENT_ANY_DATE_WINDOW.dateFrom)}
                    onChange={(value) => table.setFilter('dateFrom', value?.toISODate() ?? undefined)}
                  />
                </FilterField>
                <FilterField label="Until" className="w-36">
                  <DatePicker
                    format="dd LLL yyyy"
                    placeholder={noDateFilter && !typedTab ? SEARCH_HINT_COPY.untilDefault : SEARCH_HINT_COPY.anyDate}
                    value={pickerDate(table.params.filters.dateTo, ARRANGEMENT_ANY_DATE_WINDOW.dateTo)}
                    onChange={(value) => table.setFilter('dateTo', value?.toISODate() ?? undefined)}
                  />
                </FilterField>
              </>
            )}
          </>
        }
      />

      {/*
       * ⚠️ [C3a §2] THE LIBRARY REPLACES THE TABLE AND EVERYTHING THAT BELONGS TO IT.
       *
       * The selection bar and the grouping notice are both about ARRANGEMENT rows — a group is
       * an `arrangement_groups` document and an uploaded PA is in no group — so neither may
       * survive the swap. `setSelectedKeys([])` fires in the switch's own handler, so a
       * selection made before the swap cannot come back with the operator when they return.
       */}
      {view === 'library' ? (
        <LibraryTable canWrite={canWrite} />
      ) : (
        <>
          {selectedKeys.length > 0 && (
            /*
          Between the header and the table rather than in the header: the header's actions
          are about the PAGE and these are about the SELECTION, and a control that appears
          and disappears inside the header makes the header jitter.
        */
            <div
              role="region"
              aria-label="Selected orders"
              data-testid="selection-bar"
              className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border bg-card px-3 py-2"
            >
              <span className="text-sm">
                {GROUP_COPY.selected(selectedKeys.length)}
                {anchorDate !== null && <span className="text-muted-foreground"> · {anchorDate}</span>}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  type="text"
                  onClick={() => {
                    setSelectedKeys([]);
                  }}
                >
                  {GROUP_COPY.clear}
                </Button>
                <Tooltip
                  title={selectedKeys.length < 2 ? GROUP_COPY.needsTwo : canWrite ? undefined : READ_ONLY_TOOLTIP}
                >
                  <Button
                    type="primary"
                    disabled={!canWrite || selectedKeys.length < 2}
                    data-testid="group-these"
                    onClick={() => {
                      setGroupName(anchorDate === null ? 'New group' : `Orders on ${anchorDate}`);
                      setGroupOpen(true);
                    }}
                  >
                    {GROUP_COPY.groupThese}
                  </Button>
                </Tooltip>
              </div>
            </div>
          )}

          {/*
            ⚠️ [P7B §6a] THE DISCARD MODE'S OWN BAR — between the header and the table, the grouping
            bar's place and its reason. It states the rule the checkboxes enforce (drafts only) and the
            count; the confirmation states the count again before anything is written.
          */}
          {discardMode && (
            <div
              role="region"
              aria-label={DISCARD_MODE_COPY.region}
              data-testid="discard-bar"
              className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border bg-card px-3 py-2"
            >
              <span className="text-sm">
                {DISCARD_MODE_COPY.selected(discardRows.length)}
                <span className="text-muted-foreground"> · {DISCARD_MODE_COPY.draftsOnly}</span>
              </span>
              <div className="flex items-center gap-2">
                <Button
                  type="text"
                  disabled={discardRows.length === 0}
                  onClick={() => {
                    setDiscardPick([]);
                  }}
                >
                  {GROUP_COPY.clear}
                </Button>
                <Button
                  danger
                  type="primary"
                  data-testid="discard-selected"
                  loading={bulkDiscard.isPending}
                  disabled={discardRows.length === 0 || discardRows.length > BULK_DISCARD_MAX}
                  onClick={confirmBulkDiscard}
                >
                  {DISCARD_MODE_COPY.discard(discardRows.length)}
                </Button>
              </div>
            </div>
          )}

          {/*
            ⚠️ [P7B §6a] WHAT THE LAST BULK DISCARD DID, PER DRAFT — the refusals by name and with the
            server's own sentence, because "3 of 5 discarded" without the two reasons sends the operator
            hunting for which two.
          */}
          {discardResult && (
            <div
              role="status"
              data-testid="discard-result"
              className="flex flex-col gap-1 rounded-lg border border-border bg-card px-3 py-2 text-sm"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span>{DISCARD_MODE_COPY.outcome(discardResult.result.discarded, discardResult.result.refused)}</span>
                <Button
                  type="text"
                  size="small"
                  onClick={() => {
                    setDiscardResult(undefined);
                  }}
                >
                  {DISCARD_MODE_COPY.dismiss}
                </Button>
              </div>
              {discardResult.result.items
                .filter((item) => item.outcome === 'REFUSED')
                .map((item) => (
                  <span key={item.arrangementId} className="text-muted-foreground" data-testid="discard-refusal">
                    {discardResult.titles[item.arrangementId] ?? item.arrangementId}:{' '}
                    {item.reason ?? DISCARD_MODE_COPY.refusedNoReason}
                  </span>
                ))}
            </div>
          )}

          {!nested && <p className="text-xs text-muted-foreground">{GROUP_COPY.flatUnderSort}</p>}

          <Table
            {...table.tableProps}
            columns={columns}
            size="middle"
            scroll={{ x: 'max-content' }}
            expandable={{
              rowExpandable: (row) => row.rowKind === 'group',
              expandedRowKeys: (table.tableProps.dataSource ?? [])
                .filter((row) => row.rowKind === 'group' && !collapsedGroups.includes(row.key))
                .map((row) => row.key),
              onExpand: (expanded, row) => {
                setCollapsedGroups((current) =>
                  expanded ? current.filter((key) => key !== row.key) : [...current, row.key],
                );
              },
            }}
            /*
             * ⚠️ BOTH INVARIANTS ARE ENFORCED IN THE CHECKBOX, BEFORE THE CLICK, RATHER THAN IN
             * AN ERROR AFTER IT. One order date per group, and at most one group per order —
             * one rule, two facts, zero error paths. The anchor date is shown in the selection
             * bar, so the first rule is VISIBLE rather than merely enforced.
             */
            rowSelection={
              discardMode
                ? {
                    /*
                     * ⚠️ [P7B §6a] DRAFTS ONLY, IN THE CHECKBOX — an issued order is never discarded
                     * from here, and a draft in a corrigendum family is refused by the server (its
                     * archive would cancel the published parent), so neither is selectable.
                     */
                    selectedRowKeys: discardKeys,
                    onChange: (keys) => {
                      setDiscardPick(keys.map(String));
                    },
                    checkStrictly: true,
                    getCheckboxProps: (row) => {
                      if (row.rowKind === 'group') return { disabled: true, name: row.name };
                      if (row.status !== 'DRAFT' || row.archivedAt !== null) {
                        return { disabled: true, name: row.displayTitle, title: DISCARD_MODE_COPY.notADraft };
                      }
                      if (row.group?.kind === 'CORRIGENDUM') {
                        return { disabled: true, name: row.displayTitle, title: DISCARD_MODE_COPY.corrigendum };
                      }
                      return { name: row.displayTitle };
                    },
                  }
                : {
                    selectedRowKeys: selectedKeys,
                    onChange: (keys) => {
                      setSelectedKeys(keys.map(String));
                    },
                    checkStrictly: true,
                    getCheckboxProps: (row) => {
                      if (row.rowKind === 'group') return { disabled: true, name: row.name };
                      if (row.group)
                        return { disabled: true, name: row.displayTitle, title: GROUP_COPY.alreadyGrouped };
                      if (anchorDate !== null && row.paDate !== anchorDate) {
                        return { disabled: true, name: row.displayTitle, title: GROUP_COPY.sameDateOnly };
                      }
                      return { name: row.displayTitle };
                    },
                  }
            }
            /*
             * ⚠️ A DRAFT OPENS THE FORM; ANYTHING ELSE OPENS THE RECORD. A half-written order
             * has nothing to review, and a published one cannot have its matrix changed — so
             * sending both to the same screen would be wrong for one of them every time.
             */
            locale={{
              /*
               * ⚠️ [C5b §6] THREE CASES — no search, a hint, and a search nothing matches. The
               * reset writes `searchHintPatch` in ONE `setFilters` call, which never touches the
               * search and returns to page one, so the same term re-runs with only the filters the
               * server named cleared.
               */
              emptyText: (
                <SearchEmptyState
                  page={table.query.data}
                  kind={kind}
                  typeLabel={typedTab ? ARRANGEMENT_TYPES[typedTab].label : undefined}
                  fixedType={fixedType}
                  /*
                   * ⚠️ [LIST-FILTERS] THE RESET HAS TWO OWNERS NOW, SO IT IS PARTITIONED IN ONE HANDLER: URL
                   * keys to `setFilters`, session keys to the store. Passed whole, `divisionOrgUnitId`
                   * would be dropped by nuqs — it no longer tracks it — and "Show matches" would re-run
                   * the same empty query. `setFilters` runs even with nothing for the URL, for its page
                   * reset; both writes batch into one request.
                   */
                  onReveal={(filters) => {
                    const { url, session } = partitionSearchHintPatch(searchHintPatch(filters));
                    if (session.length > 0) listFilters.apply(relaxSessionFilters(listFilters.filters, session));
                    table.setFilters(url);
                  }}
                  onClearFilters={clearListFilters}
                  /*
                   * [TYPE-FILTER] An empty search under a narrowing type, with no hint: the hint never names `type`,
                   * so the page does — and
                   * "Show all types" widens it to All, keeping the term, back to page one in one request.
                   */
                  onShowAllTypes={() => {
                    listFilters.apply({ ...listFilters.filters, type: ARRANGEMENT_TYPE_ANY });
                    table.setFilters({});
                  }}
                />
              ),
            }}
          />

          <Modal
            open={groupOpen}
            title={GROUP_COPY.modalTitle(selectedKeys.length)}
            okText="Group"
            confirmLoading={createGroup.isPending}
            data-testid="group-modal"
            onCancel={() => {
              setGroupOpen(false);
            }}
            onOk={() => {
              createGroup.mutate({ name: groupName, arrangementIds: selectedKeys });
            }}
          >
            <div className="flex flex-col gap-3">
              <Input
                aria-label={GROUP_COPY.nameLabel}
                value={groupName}
                onChange={(event) => {
                  setGroupName(event.target.value);
                }}
              />
              {/* ⚠️ ASSERTED BY A TEST. It is the whole of what grouping promises. */}
              <p className="text-xs text-muted-foreground">{GROUP_COPY.whatItDoes}</p>
            </div>
          </Modal>

          <Modal
            open={renaming !== undefined}
            title="Rename group"
            okText="Save"
            confirmLoading={renameGroup.isPending}
            onCancel={() => {
              setRenaming(undefined);
            }}
            onOk={() => {
              if (renaming) renameGroup.mutate(renaming);
            }}
          >
            <Input
              aria-label={GROUP_COPY.nameLabel}
              value={renaming?.name ?? ''}
              onChange={(event) => {
                setRenaming((current) => (current ? { ...current, name: event.target.value } : current));
              }}
            />
          </Modal>

          <Modal
            open={copyOpen}
            onCancel={() => setCopyOpen(false)}
            footer={null}
            title="Start from a previous arrangement"
            data-testid="copy-from-previous"
          >
            <p className="mb-3 text-sm text-muted-foreground">
              Everything copies except the date, which starts at today. You can change anything before publishing.
            </p>
            <ul className="flex flex-col gap-1">
              {(recent.data?.items ?? []).map((row) => (
                <li key={row.id}>
                  <button
                    type="button"
                    className="flex w-full flex-col rounded-md px-2 py-1.5 text-left hover:bg-hover"
                    disabled={duplicate.isPending}
                    onClick={() => duplicate.mutate(row.id)}
                  >
                    <span className="text-sm">{row.displayTitle}</span>
                    <span className="text-xs text-muted-foreground">
                      {row.paDate} · {row.placeCount} place(s) · {row.deploymentCount} duties
                    </span>
                  </button>
                </li>
              ))}
              {recent.data?.items.length === 0 && (
                <li className="text-sm text-muted-foreground">There are no earlier arrangements yet.</li>
              )}
            </ul>
          </Modal>
        </>
      )}

      {/*
       * ⚠️ [C3a §2] ITS OWN COMPONENT, AND THAT IS WHAT KEEPS `census.spec.ts` UNTOUCHED ON THE
       * MODAL COUNT. This file is EXEMPT at exactly two `<Modal`s with a third swept; a fourth
       * declared here would break that arithmetic for an overlay that is trivially sweepable
       * from its own file. The Tooltip above IS a fifth on this file and is exempted with the
       * reason [D4.3] gives for the shape: a `title` of `undefined` mounts no node at all, so a
       * read-only hint is unreachable for the write-capable session every sweep runs as.
       *
       * ⚠️ MOUNTED OUTSIDE THE VIEW BRANCH so a close animation cannot be interrupted by the
       * switch — it is `open`-gated, which is the same thing the three modals above are.
       */}
      <UploadOrderModal
        open={uploadOpen}
        onClose={() => {
          setUploadOpen(false);
        }}
      />
      {/*
        ⚠️ [LIST-FILTERS] ITS OWN COMPONENT AND FILE, for exactly the census reason above — and mounted
        here, OUTSIDE the view branch, for the same reason as the upload dialog: it is `open`-gated, and
        a switch to Uploads must not cut its close short.
      */}
      <ArrangementFiltersModal
        open={filtersOpen}
        applied={listFilters.filters}
        fixedType={fixedType}
        divisionOffered={mayFilterByDivision(officer)}
        onApply={applyListFilters}
        onClear={clearListFilters}
        onClose={() => {
          setFiltersOpen(false);
        }}
      />
    </div>
  );
};

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [P7B.1 §4] MANAGE ARRANGEMENTS — the untyped list. A link shared while the typed list was a tab
 *  here (`/arrangements?type=durga-puja-2026`) is sent to that type's own address, keeping its other
 *  parameters; `view`, `kind` and `standingOrderId` go, because a typed list has none of them.
 *
 *  ⚠️ `<Navigate replace>` WHILE RENDERING, so the old address leaves no history entry and the list
 *  never asks the server anything first. An unregistered `?type=` is ignored, as it always was.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
const ArrangementsPage = (): React.ReactElement => {
  const [searchParams] = useSearchParams();
  const legacy = searchParams.get('type');
  if (isArrangementType(legacy)) {
    const rest = new URLSearchParams(searchParams);
    for (const key of ['type', 'view', 'kind', 'standingOrderId']) rest.delete(key);
    const search = rest.toString();
    return <Navigate replace to={{ pathname: typedListHref(legacy), search: search === '' ? '' : `?${search}` }} />;
  }
  return <ArrangementsList />;
};

export default ArrangementsPage;
